import app from 'flarum/common/app';
import { SPRITES } from './sprites';
import { HEROES, HERO_FRAME } from './heroes';
import { MONSTERS } from './monsters';

// Renders the tag icons and animations that can be picked in Admin → Tags:
//   icon:      sprite:<id> | hero:<id> | monster:<id> | an image URL
//   animation: hero:<id> | monster:<id> (runs across) | sparkles | embers
// Sheet sizing and cropping happen in CSS (see .PxSheet in common.less), so
// the same sprite can be drawn at any size by setting --px-size.

/** A value for CSS url(). The API only accepts URLs without quotes or brackets; quoting is a second guard. */
export function cssUrl(url) {
  return `url(${JSON.stringify(url)})`;
}
function parse(value) {
  const match = /^(sprite|hero|monster):([a-z0-9-]+)$/i.exec(value || '');
  return match ? {
    type: match[1].toLowerCase(),
    id: match[2].toLowerCase()
  } : null;
}

/** Served by HeroSpriteController (no .png, so it never hits a static-file rule), so the sheets work without publishing assets. */
export function heroSheetUrl(id, animation) {
  const base = String(app.forum.attribute('baseUrl') || '').replace(/\/+$/, '');
  return `${base}/pixmmo-pixelui/heroes/${id}-${animation}`;
}
export function monsterSheetUrl(monster, animation) {
  const base = String(app.forum.attribute('pxGameAssetsUrl') || 'https://www.pixmmo.com').replace(/\/+$/, '');
  return `${base}/Monsters/${encodeURIComponent(monster.folder)}/${encodeURIComponent(`${monster.folder}_${animation}`)}.png`;
}

/** An animated sprite sheet, cropped to a square window around the art. */
export function sheet(_ref) {
  let {
    url,
    frame,
    frames,
    fps,
    cx = frame / 2,
    cy = frame / 2,
    crop = frame,
    className = ''
  } = _ref;
  return m("span", {
    className: `PxSheet ${className}`,
    "aria-hidden": "true",
    style: {
      '--px-sheet': cssUrl(url),
      '--px-frame': frame,
      '--px-frames': frames,
      '--px-fps': fps,
      '--px-cx': cx,
      '--px-cy': cy,
      '--px-crop': crop
    }
  });
}

/** A hero's run or idle cycle (see heroes.js). */
export function heroSheet(id, animation) {
  const hero = HEROES[id];
  if (!hero) return null;
  const [frames, fps, cx, cy, crop] = hero[animation];
  return sheet({
    url: heroSheetUrl(id, animation),
    frame: HERO_FRAME,
    frames,
    fps,
    cx,
    cy,
    crop
  });
}

/** A monster's idle cycle, or its walk/fly cycle for 'move' (see monsters.js). */
export function monsterSheet(id, animation) {
  const monster = MONSTERS[id];
  if (!monster) return null;
  const [name, frames, cx, cy, crop] = animation === 'move' ? monster.move : monster.idle;
  return sheet({
    url: monsterSheetUrl(monster, name),
    frame: 100,
    frames,
    fps: 8,
    cx,
    cy,
    crop
  });
}

/** A hand-drawn PixMMO sprite as crisp inline SVG. */
export function spriteSvg(id, className) {
  if (className === void 0) {
    className = '';
  }
  const sprite = SPRITES[id];
  if (!sprite) return null;
  const {
    rows,
    palette
  } = sprite;
  const width = Math.max(...rows.map(row => row.length));
  const rects = [];
  rows.forEach((row, y) => {
    let x = 0;
    while (x < row.length) {
      const c = row[x];
      let end = x + 1;
      while (end < row.length && row[end] === c) end++;
      if (palette[c]) rects.push(m("rect", {
        x: x,
        y: y,
        width: end - x,
        height: 1,
        fill: palette[c]
      }));
      x = end;
    }
  });
  return m("svg", {
    className: `PxSpriteSvg ${className}`,
    viewBox: `0 0 ${width} ${rows.length}`,
    "shape-rendering": "crispEdges",
    "aria-hidden": "true"
  }, rects);
}

/** The tag icon for a stored value, or null for "use the Font Awesome icon". */
export function iconFor(value, className) {
  if (className === void 0) {
    className = '';
  }
  if (!value) return null;
  const ref = parse(value);
  if (!ref) return m("img", {
    className: `PxIconImage ${className}`,
    src: value,
    alt: "",
    loading: "lazy"
  });
  const art = ref.type === 'sprite' ? spriteSvg(ref.id) : ref.type === 'hero' ? heroSheet(ref.id, 'idle') : monsterSheet(ref.id, 'idle');
  return art ? m("span", {
    className: `PxIcon PxIcon--${ref.type} ${className}`
  }, art) : null;
}

/** Classes for an element hosting the animation for a stored value. */
export function animationHostClass(value) {
  if (!value) return '';
  return /^(hero|monster):/.test(value) ? 'PxAnimHost PxAnimHost--walker' : 'PxAnimHost';
}

/** The animated background layer for a stored value, or null. */
export function animationFor(value) {
  if (!value) return null;
  if (value === 'sparkles' || value === 'embers') {
    return m("span", {
      className: `PxAnim PxAnim--${value}`,
      "aria-hidden": "true"
    }, Array.from({
      length: 8
    }, (_, i) => m("i", {
      className: `PxAnim-particle PxAnim-particle--${i + 1}`
    })));
  }
  const ref = parse(value);
  if (!ref || ref.type === 'sprite') return null;
  const walker = ref.type === 'hero' ? heroSheet(ref.id, 'run') : monsterSheet(ref.id, 'move');
  if (!walker) return null;
  return m("span", {
    className: `PxAnim PxAnim--walker PxAnim--${ref.type}`,
    "aria-hidden": "true"
  }, m("span", {
    className: "PxAnim-walker"
  }, walker));
}
export { SPRITES, HEROES, MONSTERS };
flarum.reg.add('soup-97-pixmmo-pixelui', 'common/pixelArt', { cssUrl: cssUrl,heroSheetUrl: heroSheetUrl,monsterSheetUrl: monsterSheetUrl,sheet: sheet,heroSheet: heroSheet,monsterSheet: monsterSheet,spriteSvg: spriteSvg,iconFor: iconFor,animationHostClass: animationHostClass,animationFor: animationFor,SPRITES: SPRITES,HEROES: HEROES,MONSTERS: MONSTERS, });